import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * PlaceAutocomplete — reusable address autocomplete input.
 * Uses the backend proxy at /api/freight/places/autocomplete
 * to keep the Google Maps API key server-side.
 *
 * @param {Object} props
 * @param {String} props.value - Current input value
 * @param {Function} props.onChange - Called with the selected description string
 * @param {String} props.placeholder - Input placeholder
 * @param {String} props.className - CSS class for the input
 * @param {String} props.types - Google Places types filter (default: 'geocode')
 */
export default function PlaceAutocomplete({
    value,
    onChange,
    placeholder = 'Start typing an address...',
    className = 'filter-input',
    types = 'geocode'
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);

    // Sync external value
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSuggestions = useCallback(async (input) => {
        if (!input || input.length < 3) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('freight_token');
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
            const params = new URLSearchParams({ input, types });
            const res = await fetch(`${apiUrl}/api/freight/places/autocomplete?${params}`, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                }
            });
            const data = await res.json();
            if (data.success && data.data.length > 0) {
                setSuggestions(data.data);
                setShowDropdown(true);
            } else {
                setSuggestions([]);
                setShowDropdown(false);
            }
        } catch {
            setSuggestions([]);
            setShowDropdown(false);
        } finally {
            setLoading(false);
        }
    }, [types]);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        onChange(val);

        // Debounce API calls
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
    };

    const handleSelect = (suggestion) => {
        setInputValue(suggestion.description);
        onChange(suggestion.description);
        setShowDropdown(false);
        setSuggestions([]);
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <input
                type="text"
                className={className}
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => {
                    if (suggestions.length > 0) setShowDropdown(true);
                }}
                autoComplete="off"
            />
            {loading && (
                <span style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)'
                }}>
                    ...
                </span>
            )}
            {showDropdown && suggestions.length > 0 && (
                <ul style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: 'var(--bg-card, #fff)',
                    border: '1px solid var(--border-color, #ddd)',
                    borderRadius: '0.375rem',
                    maxHeight: '200px',
                    overflow: 'auto',
                    margin: '0.25rem 0 0',
                    padding: 0,
                    listStyle: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                    {suggestions.map((s) => (
                        <li
                            key={s.placeId}
                            onClick={() => handleSelect(s)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                borderBottom: '1px solid var(--border-color, #eee)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--bg-muted, #f5f5f5)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <strong>{s.mainText}</strong>
                            {s.secondaryText && (
                                <span className="text-muted" style={{ marginLeft: '0.25rem', fontSize: '0.8rem' }}>
                                    {s.secondaryText}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
